import { useEffect, useMemo, useState } from "react";
import { useHomeownerAuth } from "./useHomeownerAuth";
import HomeownerGate from "./HomeownerGate";
import { supabaseAnon as supabase } from "../../lib/supabase";
import { calcScope } from "../../lib/scopeCalc";
import { tradeMoneyOf } from "../../lib/agreementPayload";
import BankVerificationNotice, { useBankVerifications, verificationTotal } from "./BankVerificationNotice";

const CARD_SURCHARGE_BPS = 300; // 3.00% — keep in sync with create-payment-link
const FEE_FREE_CEILING_CENTS = 150_000; // $1,500 — no fee on either method below this

function fmtUSD(n: number | null | undefined) {
  if (n == null) return "$0.00";
  return "$" + Number(n).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// Slot derivation: anything from `schedule_build` onward (Chad approved the
// build → it's on the calendar / in progress / done / collecting) is treated
// as post-build. Everything else (intake through `build_submitted`) is
// pre-build = HO is paying ACV + out-of-pocket before construction starts.
// Inverted enum-set list so we naturally cover any new pre-build stages added
// later — this is the smaller, more stable set.
// Mirror of create-payment-link's POST_BUILD_STAGES.
const POST_BUILD_STAGES = new Set([
  "schedule_build", "build_scheduled", "roof_complete",
  "trades_in_progress", "trades_complete",
  "commission_ready", "commission_approved", "commission_paid",
  "register_oc_warranty",
  "ready_for_supps", "ready_for_updated_invoice",
  "supps_waiting_on_claims", "supps_waiting_on_final_invoice",
  "collecting", "delinquent", "collect_payment",
  "closed_won",
]);

type PaymentMethod = "ach" | "card";
type AmountMode = "full" | "custom";

// Banner shown when Stripe (or the zero-balance guard) redirects back to /pay.
// Jul 12, 2026 — confirmation states so the homeowner always knows whether the
// payment went through, was cancelled, or wasn't needed — and for how much.
// `amt` (total cents) + `method` are stamped on the success_url by
// create-payment-link v8; older links without them fall back to a generic
// success message.
type ReturnBanner =
  | { kind: "paid"; amountCents: number | null; method: PaymentMethod | null }
  | { kind: "cancelled" }
  | { kind: "zero_balance" };

export default function PaymentPortal() {
  const auth = useHomeownerAuth();
  const { deal } = auth;
  const [creatingLink, setCreatingLink] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  // UX state
  const [method, setMethod] = useState<PaymentMethod>("ach");
  const [amountMode, setAmountMode] = useState<AmountMode>("full");
  const [customAmount, setCustomAmount] = useState<string>("");
  const [banner, setBanner] = useState<ReturnBanner | null>(null);
  const [userPickedMode, setUserPickedMode] = useState(false);
  // Bank payments typed in by hand that are still waiting on the homeowner
  // to confirm Stripe's test deposit (Oct 20, 2026).
  const bankVerifications = useBankVerifications(deal);

  // Derive the confirmation banner from the redirect query params:
  //   ?paid=1&amt=<total cents>&method=ach|card  — Stripe success_url
  //   ?cancelled=1                               — Stripe cancel_url (backed out; no charge)
  //   ?status=zero_balance                       — create-payment-link zero-balance guard
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("paid") === "1") {
      const amt = Number(sp.get("amt"));
      const m = sp.get("method");
      setBanner({
        kind: "paid",
        amountCents: Number.isFinite(amt) && amt > 0 ? Math.round(amt) : null,
        method: m === "ach" || m === "card" ? m : null,
      });
    } else if (sp.get("cancelled") === "1") {
      setBanner({ kind: "cancelled" });
    } else if (sp.get("status") === "zero_balance") {
      setBanner({ kind: "zero_balance" });
    }
  }, []);

  // Compute the live financial picture from the same scope-calc the install
  // agreement PDF uses. This way pre-build deals (where final_invoice isn't
  // calculated yet) still know what the homeowner owes RIGHT NOW.
  const fin = useMemo(() => {
    if (!deal) {
      return {
        firstPayment: 0,
        initialInvoice: 0,
        preReceived: 0,
        postReceived: 0,
        totalReceived: 0,
        inFlight: 0,
        preInFlight: 0,
        totalPaid: 0,
        prePaid: 0,
        invoiced: 0,
        outstanding: 0,
        slot: "post_build" as "pre_build" | "post_build",
        balanceKnown: false,
        tradesDueAtCompletion: 0,
      };
    }
    // calcScope accepts Partial<Deal> | Record<string,unknown>; HomeownerDeal
    // is a Pick-like subset of Deal so a structural cast through unknown is
    // safe — calcScope only reads numeric/boolean fields all present here.
    const scope = calcScope(deal as unknown as Record<string, unknown>);
    const preReceived = Number(deal.pre_build_amount_received ?? 0);
    const postReceived = Number(deal.post_build_amount_received ?? 0);
    const totalReceived = preReceived + postReceived;
    // Payments handed over but not yet bank-cleared (check in the iink
    // pipeline, Stripe at 'made'). Counts as paid from the homeowner's
    // perspective — Jun 10, 2026 fix.
    const preInFlight = Number(deal.pre_build_amount_in_flight ?? 0);
    const postInFlight = Number(deal.post_build_amount_in_flight ?? 0);
    const inFlight = preInFlight + postInFlight;
    const totalPaid = totalReceived + inFlight;
    const prePaid = preReceived + preInFlight;
    const slot: "pre_build" | "post_build" = POST_BUILD_STAGES.has(deal.stage)
      ? "post_build"
      : "pre_build";

    // Prefer Shannon's stamped invoice number when set; else fall back to
    // calc-derived initialInvoice; else total_contract_amount.
    const invoiced = Number(
      deal.final_invoice_amount ??
      deal.updated_invoice_amount ??
      deal.initial_invoice_amount ??
      scope.initialInvoice ??
      deal.total_contract_amount ??
      0,
    );

    let outstanding: number;
    if (slot === "pre_build") {
      outstanding = Math.max(0, scope.firstPaymentToBardin - preReceived - preInFlight);
    } else {
      outstanding = Math.max(0, invoiced - totalPaid);
    }

    // Same guard as before — don't claim "Paid in Full" until there's a real
    // signal (contract/invoice set or money received).
    const balanceKnown =
      Number(deal.total_contract_amount ?? 0) > 0 ||
      Number(deal.final_invoice_amount ?? 0) > 0 ||
      Number(deal.updated_invoice_amount ?? 0) > 0 ||
      scope.firstPaymentToBardin > 0 ||
      totalPaid > 0;

    return {
      firstPayment: scope.firstPaymentToBardin,
      initialInvoice: scope.initialInvoice,
      // Oct 29, 2026: trades billed to the homeowner from the trade sheet are due
      // at completion — never in the first payment. Shown under it so the
      // homeowner isn't surprised by them on the final invoice.
      tradesDueAtCompletion: tradeMoneyOf(scope).total,
      preReceived,
      postReceived,
      totalReceived,
      inFlight,
      preInFlight,
      totalPaid,
      prePaid,
      invoiced,
      outstanding,
      slot,
      balanceKnown,
    };
  }, [deal]);

  const isFullyPaid = fin.balanceKnown && fin.outstanding <= 0 && fin.totalPaid > 0;
  // The part of "processing" that can't move until the homeowner verifies
  // their bank account. Still counted as paid, so nobody pays twice.
  const awaitingVerify = Math.min(fin.inFlight, verificationTotal(bankVerifications));
  const awaitingVerifyPre = Math.min(
    fin.preInFlight,
    verificationTotal(bankVerifications.filter((v) => v.slot === "pre_build")),
  );

  // When the calculated balance is $0 (no invoice or scope numbers yet), the
  // "Pay Full Balance" tile is meaningless — auto-flip to Custom so the button
  // stays usable. The homeowner can always type any amount they want.
  // Greg's rule (May 8, 2026): keep the Pay button live and viewable until
  // the deal is fully paid.
  useEffect(() => {
    if (userPickedMode) return;
    if (fin.outstanding > 0) setAmountMode("full");
    else setAmountMode("custom");
  }, [fin.outstanding, userPickedMode]);

  const fullBalanceAvailable = fin.outstanding > 0;

  // Resolve which dollar amount the "Pay" button will charge.
  const customCents = useMemo(() => {
    const n = Number(customAmount.replace(/[^0-9.]/g, ""));
    return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : 0;
  }, [customAmount]);

  const baseCents = useMemo(() => {
    if (amountMode === "full") return Math.round(fin.outstanding * 100);
    return customCents;
  }, [amountMode, fin.outstanding, customCents]);

  // Tiered fee: payments ≤ $1,500 are free on both methods (Bardin absorbs
  // Stripe's cost). Above that, card adds 3%.
  const feeCents = method === "card" && baseCents > FEE_FREE_CEILING_CENTS
    ? Math.round((baseCents * CARD_SURCHARGE_BPS) / 10_000)
    : 0;
  const feeFreeForThisAmount = baseCents > 0 && baseCents <= FEE_FREE_CEILING_CENTS;
  const totalCents = baseCents + feeCents;

  const customTooHigh =
    amountMode === "custom" &&
    fin.outstanding > 0 &&
    customCents > Math.round(fin.outstanding * 100);

  const canPay =
    !creatingLink &&
    baseCents >= 100 && // Stripe minimum (we floor at $1)
    !customTooHigh;

  const handlePayNow = async () => {
    if (!deal || !canPay) return;
    setCreatingLink(true);
    setLinkError(null);

    // Re-derive the URL token (the homeowner already came in via one).
    const sp = new URLSearchParams(window.location.search);
    const token = sp.get("token") || "";

    try {
      const { data, error } = await supabase.functions.invoke("create-payment-link", {
        body: {
          token,
          payment_method: method,
          amount_mode: amountMode,
          amount_cents: amountMode === "custom" ? customCents : undefined,
        },
      });
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      setLinkError("Unable to create payment link. Please call us at (470) 823-8816.");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Something went wrong.";
      setLinkError(`${msg} If the issue persists, call us at (470) 823-8816.`);
    } finally {
      setCreatingLink(false);
    }
  };

  return (
    <HomeownerGate {...auth} title="Make a Payment">
      {deal && (
        <div className="space-y-4">
          {banner?.kind === "paid" && (
            <div className="bg-green-50 border border-green-300 rounded-xl p-4 text-green-800">
              <div className="font-semibold">
                ✓ {banner.method === "ach" ? "Bank transfer started" : "Payment received"} — thank you!
              </div>
              <div className="text-sm mt-1">
                {banner.amountCents != null && (
                  <>
                    We received your payment of{" "}
                    <span className="font-semibold">{fmtUSD(banner.amountCents / 100)}</span>
                    {banner.method === "ach"
                      ? " by bank transfer (ACH)"
                      : banner.method === "card"
                      ? " by card"
                      : ""}
                    .{" "}
                  </>
                )}
                {banner.method === "ach"
                  ? (bankVerifications.length > 0
                      ? "One more step: because you typed in your bank account numbers, Stripe has to confirm the account before the payment goes through. See below."
                      : "Bank transfers typically take 3–5 business days to clear, and this payment shows under \"Amount Paid\" as processing until then. If you typed in your account and routing numbers instead of logging in to your bank, there's one more step: Stripe sends a small test deposit to your account, and you'll confirm it on this page to finish. We'll send you a receipt once the transfer clears.")
                  : "It can take a few minutes for the balance below to update. A receipt is on its way to you."}
              </div>
            </div>
          )}
          {banner?.kind === "cancelled" && (
            <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 text-amber-800">
              <div className="font-semibold">Payment not completed</div>
              <div className="text-sm mt-1">
                Your payment was cancelled before it finished — <span className="font-semibold">no charge was made</span>.
                You can try again below whenever you're ready, or call us at (470) 823-8816 if something went wrong.
              </div>
            </div>
          )}
          {banner?.kind === "zero_balance" && (
            <div className="bg-blue-50 border border-blue-300 rounded-xl p-4 text-blue-800">
              <div className="font-semibold">No payment due</div>
              <div className="text-sm mt-1">
                Good news — our records show your balance is $0.00, so <span className="font-semibold">no charge was made</span>.
                If you believe you still owe a payment, call us at (470) 823-8816.
              </div>
            </div>
          )}

          <BankVerificationNotice items={bankVerifications} />

          {/* Summary Card */}
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <h1 className="text-lg font-bold text-bardin-ink mb-1">💳 Payment Summary</h1>
            <p className="text-sm text-gray-500 mb-4">{deal.property_street}</p>

            <div className="space-y-3 text-sm">
              {fin.slot === "pre_build" ? (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{deal.is_retail ? "50% Deposit Due (before build)" : "First Payment Due (before build)"}</span>
                    <span className="font-medium">{fmtUSD(fin.firstPayment)}</span>
                  </div>
                  {fin.tradesDueAtCompletion > 0 && (
                    <div className="flex justify-between text-xs text-gray-400 -mt-1">
                      <span>Trades billed to you (due at completion, not now)</span>
                      <span>{fmtUSD(fin.tradesDueAtCompletion)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-500">Amount Paid</span>
                    <span className="font-medium text-green-600 text-right">
                      {fmtUSD(fin.prePaid)}
                      {awaitingVerifyPre > 0 && (
                        <span className="block text-xs font-semibold text-amber-700">
                          includes {fmtUSD(awaitingVerifyPre)} waiting on your bank verification
                        </span>
                      )}
                      {fin.preInFlight - awaitingVerifyPre > 0 && (
                        <span className="block text-xs font-normal text-gray-400">
                          includes {fmtUSD(fin.preInFlight - awaitingVerifyPre)} processing
                        </span>
                      )}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Total Invoice</span>
                    <span className="font-medium">{fmtUSD(fin.invoiced)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Amount Paid</span>
                    <span className="font-medium text-green-600 text-right">
                      {fmtUSD(fin.totalPaid)}
                      {awaitingVerify > 0 && (
                        <span className="block text-xs font-semibold text-amber-700">
                          includes {fmtUSD(awaitingVerify)} waiting on your bank verification
                        </span>
                      )}
                      {fin.inFlight - awaitingVerify > 0 && (
                        <span className="block text-xs font-normal text-gray-400">
                          includes {fmtUSD(fin.inFlight - awaitingVerify)} processing
                        </span>
                      )}
                    </span>
                  </div>
                </>
              )}
              <div className="border-t pt-3 flex justify-between">
                <span className="font-semibold text-bardin-ink">Balance Due</span>
                <span className={`font-bold text-lg ${
                  isFullyPaid ? (awaitingVerify > 0 ? "text-amber-700" : "text-green-600")
                  : fin.outstanding > 0 ? "text-bardin-orange"
                  : "text-gray-500"
                }`}>
                  {isFullyPaid
                    ? (awaitingVerify > 0 ? "Verify your bank to finish" : "Paid in Full")
                    : fin.outstanding > 0
                    ? fmtUSD(fin.outstanding)
                    : "Pending — final invoice not yet calculated"}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Action */}
          {isFullyPaid && awaitingVerify > 0 ? (
            <div className="bg-white rounded-xl p-5 shadow-sm text-center">
              <div className="text-4xl mb-2">🏦</div>
              <h2 className="font-bold text-amber-700 text-lg">One step left</h2>
              <p className="text-sm text-gray-500 mt-1">
                Your payments cover the balance, but {fmtUSD(awaitingVerify)} won't go through until you
                verify your bank account. Use the button above.
              </p>
            </div>
          ) : isFullyPaid ? (
            <div className="bg-white rounded-xl p-5 shadow-sm text-center">
              <div className="text-4xl mb-2">✓</div>
              <h2 className="font-bold text-green-600 text-lg">Paid in Full</h2>
              <p className="text-sm text-gray-500 mt-1">Thank you for your payment!</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl p-5 shadow-sm">
              <h2 className="font-semibold text-bardin-ink mb-3">Pay Online</h2>

              {/* Amount picker */}
              <div className="mb-4">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-2 font-semibold">
                  Amount
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => { setUserPickedMode(true); setAmountMode("full"); }}
                    disabled={!fullBalanceAvailable}
                    className={`rounded-lg border px-3 py-3 text-sm font-medium transition ${
                      amountMode === "full" && fullBalanceAvailable
                        ? "border-bardin-orange bg-bardin-orange/10 text-bardin-ink"
                        : !fullBalanceAvailable
                        ? "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
                        : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                    }`}
                  >
                    <div className="font-semibold">Pay Full Balance</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {fullBalanceAvailable ? fmtUSD(fin.outstanding) : "Not yet calculated"}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setUserPickedMode(true); setAmountMode("custom"); }}
                    className={`rounded-lg border px-3 py-3 text-sm font-medium transition ${
                      amountMode === "custom"
                        ? "border-bardin-orange bg-bardin-orange/10 text-bardin-ink"
                        : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                    }`}
                  >
                    <div className="font-semibold">Custom Amount</div>
                    <div className="text-xs text-gray-500 mt-0.5">Enter any amount</div>
                  </button>
                </div>

                {amountMode === "custom" && (
                  <div className="mt-3">
                    <label className="block text-xs text-gray-500 mb-1">Amount in USD</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={customAmount}
                        onChange={(e) => setCustomAmount(e.target.value)}
                        placeholder="0.00"
                        className="w-full border border-gray-300 rounded-lg pl-8 pr-4 py-2.5 text-base focus:ring-2 focus:ring-bardin-orange focus:border-transparent"
                      />
                    </div>
                    {customTooHigh && (
                      <p className="text-xs text-red-500 mt-1">
                        Amount can't exceed your balance of {fmtUSD(fin.outstanding)}.
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Method picker */}
              <div className="mb-4">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-2 font-semibold">
                  Payment Method
                </div>
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => setMethod("ach")}
                    className={`w-full text-left rounded-lg border px-4 py-3 transition ${
                      method === "ach"
                        ? "border-bardin-orange bg-bardin-orange/5"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-bardin-ink">🏦 Bank Transfer (ACH)</div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          Recommended — no processing fee
                        </div>
                      </div>
                      <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded">
                        FREE
                      </span>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMethod("card")}
                    className={`w-full text-left rounded-lg border px-4 py-3 transition ${
                      method === "card"
                        ? "border-bardin-orange bg-bardin-orange/5"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-bardin-ink">💳 Credit / Debit Card</div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {feeFreeForThisAmount
                            ? "No fee on payments $1,500 or less"
                            : "3% processing fee on payments over $1,500"}
                        </div>
                      </div>
                      <span className={`text-xs font-semibold px-2 py-1 rounded ${
                        feeFreeForThisAmount
                          ? "text-green-600 bg-green-50"
                          : "text-amber-700 bg-amber-50"
                      }`}>
                        {feeFreeForThisAmount ? "FREE" : "+3%"}
                      </span>
                    </div>
                  </button>
                </div>
              </div>

              {/* Total preview */}
              {baseCents > 0 && (
                <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm space-y-1">
                  <div className="flex justify-between text-gray-600">
                    <span>Payment</span>
                    <span>{fmtUSD(baseCents / 100)}</span>
                  </div>
                  {feeCents > 0 && (
                    <div className="flex justify-between text-gray-600">
                      <span>Card processing fee (3%)</span>
                      <span>{fmtUSD(feeCents / 100)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold text-bardin-ink border-t border-gray-200 pt-1 mt-1">
                    <span>Total</span>
                    <span>{fmtUSD(totalCents / 100)}</span>
                  </div>
                </div>
              )}

              <button
                onClick={handlePayNow}
                disabled={!canPay}
                className="w-full bg-bardin-orange text-white font-semibold rounded-lg py-3 hover:bg-bardin-orange/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creatingLink
                  ? "Setting up secure payment..."
                  : baseCents > 0
                  ? `Pay ${fmtUSD(totalCents / 100)} →`
                  : amountMode === "custom"
                  ? "Enter an amount"
                  : "Pay"}
              </button>

              {linkError && (
                <p className="text-red-500 text-sm mt-3 text-center">{linkError}</p>
              )}

              <p className="text-xs text-gray-400 mt-3 text-center">
                Secure payment powered by Stripe. You'll be redirected to enter your details.
              </p>

              <div className="mt-4 border-t pt-4">
                <h3 className="text-sm font-medium text-bardin-ink mb-1">Pay by Check</h3>
                <p className="text-xs text-gray-500">
                  Make checks payable to{" "}
                  <span className="font-medium">Peach Duck Contracting Company</span>.
                  Your Restore Manager can collect it, or mail to our office.
                </p>
              </div>
            </div>
          )}

          {/* Contact */}
          <div className="text-center text-sm text-gray-400 pb-6">
            Payment questions? Call or text{" "}
            <a href="tel:+14708238816" className="text-bardin-teal font-medium">
              (470) 823-8816
            </a>
          </div>
        </div>
      )}
    </HomeownerGate>
  );
}
